import React, { useCallback, useMemo, useState } from 'react'
import { submitReportProgress } from '@/enums/submitReportProgress'
import Resx from '@/components/Resx'
import { Button } from '@/components/button'
import { Input, Label } from 'reactstrap'
import { ValidationBoundary, ValidationState, Validator } from '@/components/validation'
import { PrivateKeyDropZone } from '@/report-app/pages/reporting/submissions/xmlSigning/dropzone'
import context from '@/context'
import RsaSigner from '@/utils/RsaSigner'
import { openErrorDialog } from '@/utils/dialogHelpers'
import Icon from '@/components/Icon'
import { SimpleCard } from '@/report-app/pages/reporting/submissions/submit/simpleCard'
import pako from 'pako'

function SignSubmissionCard ({ submitProgress, reportInstanceGroupId, onSubmitProgressChange }) {
  const [passphrase, setPassphrase] = useState('')
  const [privateKey, setPrivateKey] = useState(null)
  const [signing, setSigning] = useState(false)
  const [signingFailed, setSigningFailed] = useState(false)

  const cardColor = useMemo(() => signingFailed ? 'danger' : '', [signingFailed])
  const icon = useMemo(() => signingFailed ? 'bx-error' : '', [signingFailed])

  const onFileAdded = useCallback(e => setPrivateKey(e), [])
  const onPassphraseChange = useCallback(e => setPassphrase(e.target.value), [])
  const privateKeyContent = useMemo(() => privateKey ? privateKey.content : null, [privateKey])

  const onClick = async () => {
    setSigning(true)
    let xml
    try {
      /** @type {string} */
      const compressedReceivedXml = await context.api.reportSubmissionGroupService.getXmlForSigningCompressed(reportInstanceGroupId)
      const decodedDataBytes = Buffer.from(compressedReceivedXml, 'base64')
      xml = pako.inflate(decodedDataBytes, { to: 'string' })
    } catch (err) {
      setSigningFailed(true)
      openErrorDialog({
        customErrorText: 'Validation.ErrorWhileRetrievingXmlForSigning',
        customTitle: 'Reporting.XmlSigningTitle'
      })
    }

    try {
      const signer = new RsaSigner(privateKeyContent, passphrase)
      /** @type {string} */
      const signedXml = await signer.signXml(xml)

      const compressedDataBytes = pako.gzip(signedXml)
      const compressedSignedXml = Buffer.from(compressedDataBytes).toString('base64')

      const failureMessage = await context.api.reportSubmissionGroupService.submitXml({
        xml: compressedSignedXml,
        reportSubmissionGroupId: reportInstanceGroupId })

      if (!failureMessage) {
        onSubmitProgressChange(submitReportProgress.enum.Signed)
        setSigningFailed(false)
      } else {
        setSigningFailed(true)
        openErrorDialog({
          customErrorText: failureMessage,
          customTitle: 'Reporting.XmlSigningTitle'
        })
      }
    } catch (err) {
      setSigningFailed(true)
      openErrorDialog({
        customErrorText: 'Validations.XmlSigningInvalidKey',
        customTitle: 'Reporting.XmlSigningTitle'
      })
    } finally {
      setSigning(false)
    }
  }

  const isDisabled = submitProgress < submitReportProgress.enum.XmlGenerated

  return <SimpleCard dataCy='signSubmissionCard' cardColor={cardColor}
    icon={icon}
    headerText='Reporting.SignSubmission'
    submitProgress={submitProgress}
    activateSubmitProgress={submitReportProgress.enum.XmlGenerated}
    finishedMessage='Reporting.SubmissionSigned'
    beforeStartText='Reporting.SignSubmission'>
    <ValidationBoundary>
      <div>
        <Label><Resx name='Security.RsaPrivateKeyPassphrase' /></Label>
        <Validator name='passphrase' value={passphrase} required>
          <Input type='password' value={passphrase} disabled={signing} onChange={onPassphraseChange} />
        </Validator>
      </div>
      <div>
        <Label><Resx name='Security.RsaPrivateKeyFile' /></Label>
        <Validator name='privateKey' value={privateKey} required>
          <PrivateKeyDropZone
            privateKey={privateKey}
            onFileLoaded={onFileAdded} />
        </Validator>
      </div>
      <ValidationState render={({ isValid }) =>
        <Button color='primary' disabled={isDisabled || !isValid || signing} onClick={onClick}>
          <Icon name={signing ? 'loader bx-spin' : 'save'} /> &nbsp; <Resx name='Reporting.SignAndSaveXml' />
        </Button>
      } />
    </ValidationBoundary>
  </SimpleCard>
}

export { SignSubmissionCard }



// WEBPACK FOOTER //
// ./src/report-app/pages/reporting/submissions/submit/signSubmissionCard.js